import React, { Fragment, useState, useEffect } from 'react';
import { Card, useAPI, Grid, Button, Badge, Form } from 'components/lib';
import { Sidebar } from 'primereact/sidebar';
import Markdown from 'markdown-to-jsx';
import apps from '../../../constants/apps.json'; // Correctly import the JSON data
import { connectApp, findApp, findAppByConnectionId, findConnection } from './appConnectionLogic'; // Import the connection logic

export function AppConnection(props){

  const [connections, setConnections] = useState([]);

  const list = useAPI('/api/app_connection');
  const user = useAPI('/api/user');

  // const authContext = useContext(AuthContext);

  const [showSettingsPanel, setShowSettingsPanel] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(null);
  
  // Check for connect parameter in URL on mount
  useEffect(() => {
    if (user.data?.default_account) {
      // Get the connect parameter from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const connectApp = urlParams.get('connect');
      const bulkMode = urlParams.get('bulk') === 'true';
      
      if (connectApp) {
        // Verify the app exists and is a valid data app
        const app = apps.find(a => a.short_name === connectApp && a.type === 'data' && !a.coming_soon);
        
        if (app) {
          // Clear the parameters from the URL to prevent re-triggering
          window.history.replaceState({}, document.title, window.location.pathname);
          
          if (bulkMode) {
            // In bulk mode, show a special message and auto-connect
            setTimeout(() => {
              connectAppHandler(connectApp);
            }, 1000); // Slightly longer delay to show the bulk message
          } else {
            // Regular connection flow
            setTimeout(() => {
              connectAppHandler(connectApp);
            }, 500);
          }
        }
      }
    }
  }, [user.data]);
  
  // Check for bulk connection completion on mount
  useEffect(() => {
    // Check if we just completed a bulk connection (coming back from OAuth)
    const bulkState = sessionStorage.getItem('bulkConnectionState');
    if (bulkState && !window.location.search.includes('connect=')) {
      try {
        const state = JSON.parse(bulkState);
        // If we have a bulk state and no connect parameter, we likely just completed a connection
        // Show completion message and redirect back to organization list
        setTimeout(() => {
          window.location.href = '/organization?bulkComplete=true';
        }, 2000);
      } catch (error) {
        console.error('Error processing bulk state:', error);
      }
    }
  }, []);

  const appCategories = [
    {
      name: "Data Apps",
      code: "data"
    },
    {
      name: "Notifier Apps",
      code: "notification"
    },
    {
      name: "Practice Management",
      code: "pm"
    },
    {
      name: "CRM Apps",
      code: "crm"
    },
  ];

  const updateConnections = (list) => {
    if(list?.data?.length) {
      var newList = [];
      list.data.map((item) => {
        var app = findApp(item.app);
        item.icon = app.icon;
        item.short_name = item.app;
        item.org_name = item.name;
        item.name = app.name;
        item.prefix = app.prefix;
        item.edit_settings = app.edit_settings;
        newList.push(item);
        return item;
      });
      setConnections(newList);
    }
  };

  useEffect(() => {
    updateConnections(list);
  }, [list, list.data]);

  const connectAppHandler = (short_name) => {
    connectApp(short_name, user.data.default_account);
  };

  const editSettings = (conn) => {
    var app = findAppByConnectionId(conn.id, connections);
    // Setting active app
    setActiveSettings(app);
    setShowSettingsPanel(app.short_name);
  };

  const showSettings = (data, app) => {
    connectApp(app, user.data.default_account, data);
  };

  // Check if user has any data apps connected
  const hasDataApps = connections.some(conn => {
    const app = findApp(conn.short_name);
    return app && app.type === 'data' && conn.is_active;
  });

  // Only show help panel if API has loaded and there are no data apps
  const shouldShowHelp = !list.loading && connections.length >= 0 && !hasDataApps;

  // Check if we're in bulk connection mode
  const urlParams = new URLSearchParams(window.location.search);
  const bulkMode = urlParams.get('bulk') === 'true';
  const bulkState = sessionStorage.getItem('bulkConnectionState');
  let currentBulkClient = null;
  
  if (bulkMode && bulkState) {
    try {
      const state = JSON.parse(bulkState);
      currentBulkClient = state.queue[state.currentIndex];
    } catch (error) {
      console.error('Error parsing bulk state:', error);
    }
  }

  return(
    <Fragment>
        {bulkMode && currentBulkClient && (
          <Card highlight style={{ marginBottom: '30px', backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '15px' }}>
              <div style={{ fontSize: '24px', marginRight: '15px' }}>⚡</div>
              <h2 style={{ margin: 0, color: '#856404' }}>Bulk Connection in Progress</h2>
            </div>
            <p style={{ fontSize: '16px', marginBottom: '15px', lineHeight: '1.5', color: '#856404' }}>
              You're connecting <strong>{currentBulkClient.name}</strong> as part of your bulk client setup.
              After this connection completes, you'll automatically return to continue with the next client.
            </p>
            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                <strong>💡 Tip:</strong> The connection will start automatically in a moment. 
                Once you complete the authorization, you'll be brought back here to continue with the remaining clients.
              </p>
            </div>
          </Card>
        )}
        
        {shouldShowHelp && !bulkMode && (
          <Card highlight style={{ marginBottom: '30px', backgroundColor: '#f8f9fa', border: '2px solid #007bff' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '15px' }}>
              <div style={{ fontSize: '24px', marginRight: '15px' }}>📊</div>
              <h2 style={{ margin: 0, color: '#007bff' }}>Get Started with Your Data Connection</h2>
            </div>
            <p style={{ fontSize: '16px', marginBottom: '15px', lineHeight: '1.5' }}>
              To begin analyzing your financial data with LedgerView, you'll need to connect a data source. 
              This allows us to import your accounting records and provide health scores and insights.
            </p>
            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>How to connect:</h3>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Choose a Data App below</strong> - Look for QuickBooks Online, Xero, or other accounting platforms
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Click "Connect"</strong> - Follow the prompts to authorize access to your accounting data
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Start analyzing</strong> - Once connected, return to your dashboard to see insights
                </li>
              </ol>
            </div>
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              <strong>Need help?</strong>&nbsp;
              <button onClick={() => window.location.href = '/help'} style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}>Reach out</button> for support.
            </p>
          </Card>
        )}
        
        {appCategories.map((cat) => {
          return(
            <Fragment key={cat.code}>
              <h1 style={{paddingTop: 40, paddingBottom: 10}}>{cat.name}</h1>
              <Grid cols="3">
                {apps.filter(app => app.type === cat.code).map((item) => {
                  var activeConn = findConnection(item, connections);
                  return(
                    <Card key={item.short_name} highlight={activeConn !== undefined}>
                      <div style={{display: 'flex', flexDirection: 'row'}}>
                        <img style={{height: 40, width: 40}} src={item.icon} alt="" />
                        <div style={{paddingTop: '6px', fontSize: '20px', fontWeight: 'bold', paddingLeft: '20px', width: '70%'}}>{item.name}</div>
                        {activeConn === undefined ? 
                          <div style={{minHeight: 30, paddingTop: '10px', width: '120px'}}>
                            {item.coming_soon ? 'Coming soon' : <button onClick={() => connectAppHandler(item.short_name)} style={{background: 'none', border: 'none', color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>Connect</button>}
                          </div>
                          :
                          <div style={{minHeight: 30, paddingTop: '10px'}}>
                            <button onClick={() => editSettings(activeConn)} style={{background: 'none', border: 'none', color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>Settings</button>
                          </div>
                        }
                      </div>
                    </Card>
                  );
                })}
              </Grid>
            </Fragment>
          );
        })}

      {showSettingsPanel &&
      <Sidebar visible={showSettingsPanel !== null} position="right" onHide={() => setShowSettingsPanel(null)} style={{width: '40%'}}>
        <div style={{display: 'flex', flexDirection: 'row', paddingBottom: "20px"}}>
          <img style={{height: 40, width: 40}} src={activeSettings?.icon} alt="" />
          <div style={{paddingTop: '6px', fontSize: '20px', fontWeight: 'bold', paddingLeft: '20px', width: '70%'}}>{activeSettings?.name}</div>
        </div>
        <Badge color={activeSettings?.is_active === true ? "green" : "orange"} text={activeSettings?.is_active ? "Connected" : "Not Connected"} />
        {activeSettings?.is_active &&
        <>
          <table style={{marginTop: "10px", marginBottom: "10px"}}>
            <tr style={{height: "30px"}}>
              <td>Connected Since: </td>
              <td>{new Date(activeSettings.created_at).toLocaleDateString("en-US", {hour12: true}) + " " + new Date(activeSettings.created_at).toLocaleTimeString("en-US", {hour12: true})}</td>
            </tr>
            <tr>
              <td>Last Activity:</td>
              <td>{new Date(activeSettings.updated_at).toLocaleDateString("en-US", {hour12: true}) + " " + new Date(activeSettings.updated_at).toLocaleTimeString("en-US", {hour12: true})}</td>
            </tr>
          </table>
          <Button action={() => disconnectConfirm ? setDisconnectConfirm(null) : setDisconnectConfirm(activeSettings.id)} small text="Disconnect" color="blue"></Button>
          {disconnectConfirm &&
          <>
            <Card highlight>
              <h2>Are you sure you want to disconnect {activeSettings?.name}?</h2>
              <Form 
                title={'Disconnect Application'}
                buttonText={'Yes, Disconnect'}
                cancel={() => setDisconnectConfirm(null)}
                cancelButton="No, Cancel"
                url={'/api/app_connection/disconnect/' + disconnectConfirm}
                method={'PATCH'}
                destructive={false}
                text={'Are you sure you want to disconnect this application? '}
              />
            </Card>
          </>
          }
        </>
        }
        {activeSettings.settings?.form ?
          <>
            <div style={{paddingTop: '30px'}}>
              <h3>Settings</h3>
            </div>
            <Form 
              data={activeSettings.settings?.form}
              buttonText={activeSettings?.is_active ? "Update" : "Connect"}
              submit={(data) => showSettings(data, activeSettings.short_name)}
            />
          </>
          : 
          ((activeSettings === undefined) || (JSON.stringify(activeSettings) === '{}')) &&
          <>
            <div style={{paddingTop: '20px'}}></div>
            <button onClick={() => connectAppHandler(activeSettings.short_name)} style={{background: 'none', border: 'none', color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
              {activeSettings.short_name === 'qbo' ? 
                <img style={{height: 30}} src='/images/C2QB_green_btn_short_default_2x.png' alt="" /> : 
                "Connect"
              }
            </button>
          </>
        }
        {activeSettings?.description &&
        <p>
          <h3>Details</h3>
          <Markdown>{activeSettings?.description}</Markdown>
        </p>
        }
      </Sidebar>
      }
    </Fragment>
  );
}
